import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import RippleButton from "@/components/motion/RippleButton";
import { sparkFromEvent } from "@/components/motion/ClickSpark";

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep","Puducherry",
];

const inputCls = (err) => cn(
  "h-12 w-full rounded-xl border bg-white px-4 font-body text-[16px] text-brand-ink focus:outline-none focus:ring-2",
  err ? "border-red-600 focus:ring-red-600" : "border-black/15 focus:ring-brand-gold"
);

function Field({ label, error, hint, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 font-body text-[13px] font-medium text-brand-ink">{label}</span>
      {children}
      {error ? <span role="alert" className="block mt-1.5 font-body text-xs text-red-700">{error}</span>
        : hint ? <span className="block mt-1.5 font-body text-xs text-ink-muted">{hint}</span> : null}
    </label>
  );
}

/**
 * Pre-order: reserve a package now, pay only after we confirm. Collects name,
 * email, mobile, shipping address, package and quantity.
 */
export default function PreorderForm({ products = [], initialVariantId, onDone }) {
  const options = useMemo(
    () => products.flatMap((p) => (p.variants || []).map((v) => ({ id: v.id, label: `${p.name.replace("Karijeeva ", "")}, ${v.size}`, price: v.price }))),
    [products]
  );
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", line1: "", line2: "", pincode: "", city: "", state: "",
    variant_id: initialVariantId || "", quantity: 1, note: "", contact_consent: false, website: "",
  });
  const variantId = f.variant_id || options[0]?.id || "";
  const chosen = options.find((o) => o.id === variantId);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const lookupPin = async () => {
    if (!/^\d{6}$/.test(f.pincode)) return;
    try {
      const { data } = await api.get(`/geo/pincode/${f.pincode}`);
      setF((x) => ({ ...x, city: x.city || data.city, state: x.state || data.state }));
    } catch { /* convenience only */ }
  };

  const validate = () => {
    const e = {};
    if (f.full_name.trim().length < 2) e.full_name = "Tell us who the order is for.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Enter a valid email.";
    if (f.phone.replace(/\D/g, "").slice(-10).length !== 10) e.phone = "Enter a 10 digit mobile number.";
    if (f.line1.trim().length < 3) e.line1 = "Add your house or flat number and street.";
    if (!/^\d{6}$/.test(f.pincode)) e.pincode = "Pincode is 6 digits.";
    if (f.city.trim().length < 2) e.city = "Add your city.";
    if (!f.state) e.state = "Choose your state.";
    if (!variantId) e.variant_id = "Choose a package.";
    if (!f.contact_consent) e.contact_consent = "We need your permission to contact you about this pre-order.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/preorders", {
        full_name: f.full_name, email: f.email, phone: f.phone,
        address: { line1: f.line1, line2: f.line2, city: f.city, state: f.state, pincode: f.pincode },
        variant_id: variantId, quantity: Number(f.quantity), note: f.note,
        contact_consent: f.contact_consent, website: f.website,
      });
      track("generate_lead", { lead_source: "preorder", value: (chosen?.price || 0) * f.quantity, currency: "INR" });
      setDone(data);
      onDone?.(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrors({ form: typeof d === "string" ? d : "Something went wrong. Please try again or call us." });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-tile bg-white p-8 text-center" data-testid="preorder-done">
        <div className="mx-auto h-14 w-14 rounded-full bg-brand-gold flex items-center justify-center"><Check className="w-7 h-7 text-white" /></div>
        <p className="mt-5 font-display text-[28px] tracking-[-0.02em] text-brand-ink">You are on the list.</p>
        <p className="mt-2 font-body text-ink-muted">Pre-order <strong className="text-brand-ink">{done.preorder_number}</strong> for {chosen?.label} × {f.quantity}.</p>
        <p className="mt-4 font-body text-[15px] leading-relaxed text-ink-muted max-w-md mx-auto">
          Nothing has been charged. When the next batch is pressed and passes its lab test, we will message you on WhatsApp or email with a secure payment link. You can cancel any time before paying by replying to that message.
        </p>
        <Link to="/lab-reports" className="mt-6 inline-flex font-body text-[15px] text-brand-gold underline">While you wait, see how we test every batch</Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-tile bg-white p-6 sm:p-8 space-y-5" data-testid="preorder-form">
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <Field label="Package" error={errors.variant_id}>
          <select value={variantId} onChange={set("variant_id")} className={inputCls(errors.variant_id)} data-testid="preorder-variant">
            {options.map((o) => <option key={o.id} value={o.id}>{o.label} · ₹{o.price}</option>)}
          </select>
        </Field>
        <Field label="Quantity">
          <div className="inline-flex items-center h-12 rounded-xl border border-black/15 bg-white">
            <button type="button" aria-label="Less" onClick={() => setF((x) => ({ ...x, quantity: Math.max(1, x.quantity - 1) }))} className="w-11 h-full flex items-center justify-center"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center font-display text-lg" data-testid="preorder-qty">{f.quantity}</span>
            <button type="button" aria-label="More" onClick={() => setF((x) => ({ ...x, quantity: Math.min(20, x.quantity + 1) }))} className="w-11 h-full flex items-center justify-center"><Plus className="w-4 h-4" /></button>
          </div>
        </Field>
      </div>
      {chosen && <p className="-mt-2 font-body text-[13px] text-ink-muted">Current price ₹{chosen.price * f.quantity}, inclusive of taxes. You pay only after we confirm.</p>}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" error={errors.full_name}><input autoComplete="name" value={f.full_name} onChange={set("full_name")} className={inputCls(errors.full_name)} data-testid="preorder-name" /></Field>
        <Field label="Mobile number" error={errors.phone} hint="We confirm on WhatsApp or a call."><input type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} className={inputCls(errors.phone)} data-testid="preorder-phone" /></Field>
      </div>
      <Field label="Email" error={errors.email}><input type="email" inputMode="email" autoComplete="email" value={f.email} onChange={set("email")} className={inputCls(errors.email)} data-testid="preorder-email" /></Field>
      <Field label="Pincode" error={errors.pincode} hint="We fill in city and state for you.">
        <input inputMode="numeric" maxLength={6} autoComplete="postal-code" value={f.pincode} onChange={(e) => setF((x) => ({ ...x, pincode: e.target.value.replace(/\D/g, "") }))} onBlur={lookupPin} className={cn(inputCls(errors.pincode), "sm:max-w-[200px]")} data-testid="preorder-pincode" />
      </Field>
      <Field label="House, flat, street" error={errors.line1}><input autoComplete="address-line1" value={f.line1} onChange={set("line1")} className={inputCls(errors.line1)} data-testid="preorder-line1" /></Field>
      <Field label="Area, landmark (optional)"><input autoComplete="address-line2" value={f.line2} onChange={set("line2")} className={inputCls()} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="City" error={errors.city}><input autoComplete="address-level2" value={f.city} onChange={set("city")} className={inputCls(errors.city)} data-testid="preorder-city" /></Field>
        <Field label="State" error={errors.state}>
          <select value={f.state} onChange={set("state")} className={inputCls(errors.state)} data-testid="preorder-state">
            <option value="">Choose</option>
            {STATES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Anything we should know? (optional)"><textarea rows={2} maxLength={500} value={f.note} onChange={set("note")} className={cn(inputCls(), "h-auto py-3")} /></Field>
      <label className="flex items-start gap-3 font-body text-[14px] text-brand-ink">
        <input type="checkbox" checked={f.contact_consent} onChange={set("contact_consent")} className="mt-1 h-4 w-4 accent-[#2E7D4F]" data-testid="preorder-consent" />
        <span>Contact me on WhatsApp, phone or email about this pre-order. We use these details only for this order. <Link to="/privacy-policy" className="underline">Privacy policy</Link></span>
      </label>
      {errors.contact_consent && <p role="alert" className="-mt-3 font-body text-xs text-red-700">{errors.contact_consent}</p>}
      {errors.form && <p role="alert" className="font-body text-sm text-red-700">{errors.form}</p>}
      <RippleButton type="submit" disabled={busy} onClick={sparkFromEvent} className="w-full h-13 py-3.5 rounded-control bg-brand-gold hover:bg-brand-gold-deep font-body text-[16px] text-white disabled:opacity-60" data-testid="preorder-submit">
        {busy ? "Reserving" : "Reserve my oil. Pay later."}
      </RippleButton>
      <p className="text-center font-body text-xs text-ink-muted">No payment now. No card details asked. Cancel any time before you pay.</p>
    </form>
  );
}
